import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Save, Search, Globe } from 'lucide-react';
import { NAVGRAH_SHANTI_DATA, NGS_LABELS } from './NavgrahShantiData';
import { pickLang, LANGUAGE_OPTIONS } from './i18n/languageOptions';
import useIsMobile from '../../../hooks/useIsMobile';
import { useLeaveConfirm } from './Configure/components/ConfirmLeaveDialog';

// NOTE: was a flat, unscoped key shared across every one of a user's projects
// — scoped per-project below (component-level const shadows this).
// v2 → v3: saved richHtml was English-only; bump forces one regeneration so
// the multilingual (pickLang) build takes over.
const HTML_VERSION = 3;

/* ── Build structured HTML from graha fields (per selected language) ── */
export const buildInitialHtml = (d, lang = 'en') => {
  const L = (k) => pickLang(NGS_LABELS[k], lang);
  const parts = [];
  const beej   = pickLang(d.beejMantra, lang);
  const shant  = pickLang(d.shantMantra, lang);
  const jain   = pickLang(d.jainMantra, lang);
  const dir    = pickLang(d.japDirection, lang);
  const timing = pickLang(d.japaTiming, lang);
  const ratna  = pickLang(d.ratna, lang);
  const daan   = pickLang(d.daan, lang);
  if (beej)        parts.push(`<h2>${L('beejMantra')}</h2><p>${beej}</p>`);
  if (shant)       parts.push(`<h2>${L('shantMantra')}</h2><p>${shant}</p>`);
  if (jain)        parts.push(`<h2>${L('jainMantra')}</h2><p>${jain}</p>`);
  if (dir)         parts.push(`<h2>${L('japDirection')}</h2><p>${dir}</p>`);
  if (d.japaCount) parts.push(`<h2>${L('japaCount')}</h2><p>${d.japaCount}</p>`);
  if (timing)      parts.push(`<h2>${L('japaTiming')}</h2><p>${timing}</p>`);
  if (ratna)       parts.push(`<h2>${L('ratna')}</h2><p>${ratna}</p>`);
  if (daan)        parts.push(`<h2>${L('daan')}</h2><p>${daan}</p>`);
  return parts.join('') || '<p><br></p>';
};

// True while the saved html still matches the auto-build in ANY language
// (user hasn't hand-edited it) — safe to regenerate on a language switch.
export const isStillDefaultHtml = (d, html) => {
  if (!html) return true;
  return LANGUAGE_OPTIONS.some(({ code }) => buildInitialHtml(d, code) === html);
};

const migrateGraha = (d, lang = 'en') => {
  if (d.richHtml !== undefined && d._htmlVersion === HTML_VERSION) return d;
  return { ...d, richHtml: buildInitialHtml(d, lang), _htmlVersion: HTML_VERSION };
};

/* ── Toggle — same as VastuDevtaGuide ───────────────────────────── */
const Toggle = ({ checked, onChange }) => (
  <label style={{ position:'relative', display:'inline-block', width:44, height:24, cursor:'pointer', flexShrink:0 }}>
    <input type="checkbox" checked={checked} onChange={onChange} style={{ opacity:0, width:0, height:0 }} />
    <span style={{ position:'absolute', inset:0, background:checked?'#00A19D':'#CBD5E1', borderRadius:24, transition:'background .2s' }} />
    <span style={{ position:'absolute', top:2, left:checked?22:2, width:20, height:20, background:'#fff', borderRadius:'50%', transition:'left .2s', boxShadow:'0 1px 4px rgba(0,0,0,.18)' }} />
  </label>
);

/* ── Rich Text Editor ─────────────────────────────────────────────── */
const RichEditor = React.memo(function RichEditor({ value, onChange, itemId }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || '<p><br></p>';
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const exec = useCallback((cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const formatBlock = useCallback((tag) => {
    editorRef.current?.focus();
    document.execCommand('formatBlock', false, tag);
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const handleInput = useCallback(() => {
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const btn = (extra = {}) => ({
    border: '1px solid #e8eaed', borderRadius: 4,
    background: '#fff', color: '#3c4043', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: 26, padding: '0 7px', fontSize: '0.74rem', fontFamily: 'inherit',
    ...extra,
  });

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      <div style={{ display:'flex', alignItems:'center', gap:3, padding:'5px 8px', background:'#fafafa', borderBottom:'1px solid #e8eaed', flexWrap:'wrap' }}>
        <select
          onMouseDown={e => e.preventDefault()}
          onChange={e => { formatBlock(e.target.value); e.target.value = ''; }}
          defaultValue=""
          style={{ fontSize:'0.71rem', fontWeight:600, border:'1px solid #e8eaed', borderRadius:4, padding:'2px 6px', color:'#3c4043', background:'#fff', cursor:'pointer', outline:'none', fontFamily:'inherit', height:26 }}
        >
          <option value="" disabled>Format</option>
          <option value="p">Paragraph</option>
          <option value="h1">H1 — Main Heading</option>
          <option value="h2">H2 — Sub Heading</option>
          <option value="h3">H3 — Title</option>
        </select>
        <span style={{ width:1, height:16, background:'#e8eaed', margin:'0 2px' }} />
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('bold');}}      style={btn({fontWeight:800,width:26,padding:0})}>B</button>
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('italic');}}    style={btn({fontStyle:'italic',width:26,padding:0})}>I</button>
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('underline');}} style={btn({textDecoration:'underline',width:26,padding:0})}>U</button>
        <span style={{ width:1, height:16, background:'#e8eaed', margin:'0 2px' }} />
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('insertOrderedList');}}   style={btn()}>1. List</button>
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('insertUnorderedList');}} style={btn()}>• List</button>
        <span style={{ flex:1 }} />
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('removeFormat');}} style={btn({color:'#80868b',fontSize:'0.67rem'})}>Clear</button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        data-placeholder="Type here… use toolbar to format"
        className="ngs-editor"
        style={{ minHeight:160, padding:'12px 14px', outline:'none', fontFamily:"'Inter','Segoe UI',system-ui,sans-serif", fontSize:'0.84rem', color:'#202124', lineHeight:1.65 }}
      />
    </div>
  );
});

/* ── GrahaCard — same pattern as DevtaCard ───────────────────────── */
const GrahaCard = React.memo(function GrahaCard({ graha, onToggle, onHtmlChange, language = 'en' }) {
  // See the [language] effect below — hand-edited content is intentionally
  // never auto-retranslated, so this badge is the only signal the user gets.
  const isEdited = !isStillDefaultHtml(graha, graha.richHtml);
  return (
    <div style={{
      background:'#fff', borderRadius:12,
      border:`1px solid ${graha.enabled ? '#00A19D' : '#e2e8f0'}`,
      boxShadow: graha.enabled ? '0 1px 12px rgba(0,161,157,.08)' : '0 1px 3px rgba(0,0,0,.04)',
      overflow:'hidden', transition:'border-color .2s',
    }}>
      <div style={{
        display:'flex', alignItems:'center', gap:12, padding:'11px 14px',
        background: graha.enabled ? '#f0fdfc' : '#fafafa',
        borderBottom:'1px solid #f1f5f9',
      }}>
        <div style={{ width:30, height:30, borderRadius:7, flexShrink:0, background:graha.enabled?'#00A19D':'#e2e8f0', color:graha.enabled?'#fff':'#94a3b8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.76rem', fontWeight:700 }}>
          {graha.id}
        </div>
        <span style={{ flex:1, fontSize:'0.88rem', fontWeight:700, color:graha.enabled?'#0f172a':'#94a3b8', letterSpacing:'-0.01em', minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {graha.name}
        </span>
        <div style={{ display:'flex', alignItems:'center', gap:7, flexShrink:0 }}>
          {isEdited && (
            <span
              title="Edited — won't auto-translate when you switch language. Edit it again in each language you need."
              style={{ fontSize:'0.62rem', color:'#b45309', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:4, padding:'1px 6px', fontWeight:700 }}
            >
              Edited
            </span>
          )}
          <span style={{ fontSize:'0.68rem', color:graha.enabled?'#00857E':'#94a3b8', fontWeight:600 }}>
            {graha.enabled ? 'ON' : 'OFF'}
          </span>
          <Toggle checked={graha.enabled} onChange={onToggle} />
        </div>
      </div>
      <div style={{ padding:'12px 14px 14px' }}>
        <RichEditor itemId={`${graha.id}-${language}`} value={graha.richHtml} onChange={onHtmlChange} />
      </div>
    </div>
  );
});

/* ── Main Component — fully controlled by openModal prop ─────────── */
export default function NavgrahShanti({ openModal = false, onModalClose = null, onReportGenerated = null, projectId = null, language = 'en' }) {
  // Mobile: this modal's own header/toolbar/grid were desktop-only —
  // the badges + 2 header buttons overflowed a phone header, the grid's
  // minmax(560px,...) columns never fit at all, and one full card
  // (560px min) blew past the viewport width. Fixed below via isMobile-
  // gated styles only; desktop keeps every original style untouched.
  const isMobile = useIsMobile();
  const STORAGE_KEY = `navgrah_shanti_data_${projectId || 'new'}`;
  const [grahaData, setGrahaData] = useState([]);
  const [search, setSearch]       = useState('');
  const [isSaving, setIsSaving]   = useState(false);
  const [savedMsg, setSavedMsg]   = useState('');
  const scrollRef = useRef(null);

  // Load data on mount / project change
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setGrahaData(NAVGRAH_SHANTI_DATA.map(d => {
          const ov = parsed.find(p => p.id === d.id);
          return migrateGraha(ov ? { ...d, ...ov } : { ...d, enabled: false }, language);
        }));
      } else {
        setGrahaData(NAVGRAH_SHANTI_DATA.map(d => migrateGraha({ ...d, enabled: false }, language)));
      }
    } catch {
      setGrahaData(NAVGRAH_SHANTI_DATA.map(d => migrateGraha({ ...d, enabled: false }, language)));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Language switch: re-translate every graha whose text is still the
  // auto-built default (hand-edited ones are left untouched), same pattern
  // as Vastu Devta Guide / Zone Analysis / Feng Shui.
  useEffect(() => {
    setGrahaData(prev => {
      if (prev.length === 0) return prev;
      let changed = false;
      const next = prev.map(d => {
        if (!isStillDefaultHtml(d, d.richHtml)) return d;
        const translated = buildInitialHtml(d, language);
        if (translated === d.richHtml) return d;
        changed = true;
        return { ...d, richHtml: translated };
      });
      return changed ? next : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const handleToggle    = id  => setGrahaData(p => p.map(d => d.id === id ? { ...d, enabled: !d.enabled } : d));
  const handleToggleAll = val => setGrahaData(p => p.map(d => ({ ...d, enabled: val })));
  const handleHtml      = useCallback((id, html) => {
    setGrahaData(p => p.map(d => d.id === id ? { ...d, richHtml: html } : d));
  }, []);

  const handleSave = () => {
    setIsSaving(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(grahaData));
      if (onReportGenerated) onReportGenerated({ reportData: grahaData });
      setSavedMsg('All changes saved');
      setTimeout(() => setSavedMsg(''), 3000);
    } catch { setSavedMsg('Save failed'); }
    setIsSaving(false);
  };

  const handleClose = () => {
    if (onModalClose) onModalClose();
  };

  const handleSaveAndClose = () => {
    handleSave();
    handleClose();
  };

  const filtered = useMemo(() =>
    grahaData.filter(d =>
      !search
        || d.name.toLowerCase().includes(search.toLowerCase())
        || (d.direction || '').toLowerCase().includes(search.toLowerCase())
    ), [grahaData, search]);

  const enabledCount = grahaData.filter(d => d.enabled).length;

  // Fully controlled — parent decides visibility
  const leave = useLeaveConfirm({ onSave: handleSaveAndClose, onLeave: handleClose });

  if (!openModal) return null;

  return (
    /* z-index bumped above the mobile bottom toolbar (10100)/sheets — this
       used to render behind them on phone, looking like it never opened. */
    <div style={{ position:'fixed', inset:0, zIndex:10500, display:'flex', flexDirection:'column', background:'#f1f5f9', fontFamily:"'Inter','Segoe UI',system-ui,sans-serif", textAlign:'left' }}>

      <style>{`
        .ngs-editor h1 { font-size:1.0rem; font-weight:800; color:#0f172a; margin:14px 0 2px; padding:0; line-height:1.3; border-bottom:1.5px solid #e2e8f0; padding-bottom:4px; }
        .ngs-editor h1:first-child { margin-top:0; }
        .ngs-editor h2 { font-size:0.82rem; font-weight:700; color:#1e293b; margin:8px 0 1px; padding:0; line-height:1.4; }
        .ngs-editor h3 { font-size:0.8rem; font-weight:600; color:#334155; margin:6px 0 1px; line-height:1.4; }
        .ngs-editor p  { font-size:0.82rem; color:#475569; margin:1px 0 5px; line-height:1.65; }
        .ngs-editor ul, .ngs-editor ol { font-size:0.82rem; color:#475569; padding-left:1.8em; margin:2px 0 6px; line-height:1.65; }
        .ngs-editor li { margin-bottom:2px; }
        .ngs-editor strong { font-weight:700; color:#1e293b; }
        .ngs-editor em    { font-style:italic; }
        .ngs-editor:empty::before { content:attr(data-placeholder); color:#cbd5e1; pointer-events:none; }
        .ngs-editor:focus { outline:none; }
      `}</style>

      {/* Header — same dark style as VastuDevtaGuide */}
      <div style={{ background:'#0f172a', color:'#fff', padding: isMobile ? '0 14px' : '0 24px', display:'flex', alignItems:'center', justifyContent:'space-between', height:56, flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap: isMobile ? 10 : 14, minWidth:0 }}>
          <div style={{ width:32, height:32, borderRadius:8, background:'linear-gradient(135deg,#00857E,#00C4BF)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><Globe size={16} color="#fff" /></div>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:'0.94rem', fontWeight:700, lineHeight:1.2 }}>Navgrah Shanti</div>
            {!isMobile && <div style={{ fontSize:'0.68rem', color:'#64748b' }}>9 Planetary Energies &amp; Vastu Alignment</div>}
          </div>
          {!isMobile && (
            <>
              <div style={{ width:1, height:26, background:'rgba(255,255,255,.1)', margin:'0 4px' }} />
              <span style={{ fontSize:'0.72rem', background:'rgba(0,161,157,.2)', color:'#4ffffa', border:'1px solid rgba(0,161,157,.35)', borderRadius:20, padding:'2px 10px', fontWeight:600 }}>{enabledCount} enabled</span>
              <span style={{ fontSize:'0.72rem', background:'rgba(255,255,255,.06)', color:'#94a3b8', border:'1px solid rgba(255,255,255,.1)', borderRadius:20, padding:'2px 10px', fontWeight:600 }}>{9 - enabledCount} disabled</span>
            </>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap: isMobile ? 6 : 8, flexShrink:0 }}>
          {savedMsg && !isMobile && <span style={{ fontSize:'0.76rem', color:'#4ade80', fontWeight:500 }}>✓ {savedMsg}</span>}
          {isMobile ? (
            <button onClick={handleSave} disabled={isSaving} title={isSaving ? 'Saving…' : 'Save Report'}
              style={{ background:'#00A19D', border:'none', color:'#fff', borderRadius:7, width:34, height:34, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', opacity:isSaving?.7:1, flexShrink:0 }}>
              <Save size={14} />
            </button>
          ) : (
            <>
              <button onClick={handleSave} disabled={isSaving}
                style={{ background:'#00A19D', border:'none', color:'#fff', borderRadius:7, padding:'7px 18px', fontWeight:600, fontSize:'0.8rem', cursor:'pointer', display:'flex', alignItems:'center', gap:5, opacity:isSaving ? .7 : 1 }}>
                <Save size={13} />{isSaving ? 'Saving…' : 'Save Report'}
              </button>
              <button onClick={handleSaveAndClose}
                style={{ background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.12)', color:'#fff', borderRadius:7, padding:'7px 18px', fontWeight:600, fontSize:'0.8rem', cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}>
                <Save size={13} />Save &amp; Close
              </button>
            </>
          )}
          <button onClick={leave.requestClose}
            style={{ background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.1)', color:'#94a3b8', borderRadius:7, padding: isMobile ? '9px' : '7px 9px', cursor:'pointer', display:'flex', alignItems:'center', flexShrink:0 }}>
            <X size={15} />
          </button>
          {leave.dialog}
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ background:'#fff', borderBottom:'1px solid #e2e8f0', padding: isMobile ? '8px 14px' : '8px 24px', display:'flex', alignItems:'center', gap:10, flexShrink:0, flexWrap:'wrap' }}>
        <div style={{ position:'relative', flex: isMobile ? '1 1 100%' : '0 0 220px' }}>
          <Search size={13} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:'#94a3b8' }} />
          <input type="text" placeholder="Search planet name…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ width:'100%', boxSizing:'border-box', paddingLeft:28, paddingRight:8, height:34, border:'1.5px solid #e2e8f0', borderRadius:7, fontSize:'0.78rem', color:'#334155', outline:'none', fontFamily:'inherit', background:'#f8fafc' }} />
        </div>
        {!isMobile && <div style={{ width:1, height:22, background:'#e2e8f0' }} />}
        {(() => {
          const allEnabled  = grahaData.length > 0 && grahaData.every(d => d.enabled);
          const allDisabled = grahaData.length > 0 && grahaData.every(d => !d.enabled);
          return (
            <>
              <button onClick={() => handleToggleAll(true)}
                style={allEnabled ? tbBtn('#00A19D', '#fff') : tbBtn('#fff', '#64748b', '#e2e8f0')}>Enable All</button>
              <button onClick={() => handleToggleAll(false)}
                style={allDisabled ? tbBtn('#00A19D', '#fff') : tbBtn('#fff', '#64748b', '#e2e8f0')}>Disable All</button>
            </>
          );
        })()}
        <div style={{ marginLeft: isMobile ? 0 : 'auto', fontSize:'0.76rem', color:'#94a3b8', fontWeight:500 }}>
          {filtered.length} of 9 planets
        </div>
      </div>

      {/* Cards Grid */}
      <div ref={scrollRef} style={{ flex:1, overflowY:'auto', padding: isMobile ? '14px 14px 30px' : '20px 24px 40px' }}>
        <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill,minmax(560px,1fr))', gap: isMobile ? 12 : 18 }}>
          {filtered.map(graha => (
            <GrahaCard
              key={graha.id}
              graha={graha}
              language={language}
              onToggle={() => handleToggle(graha.id)}
              onHtmlChange={html => handleHtml(graha.id, html)}
            />
          ))}
        </div>
        {filtered.length === 0 && (
          <div style={{ padding:'60px 20px', color:'#94a3b8', textAlign:'center' }}>
            <div style={{ fontSize:'2rem', marginBottom:10 }}>🔍</div>
            <div style={{ fontSize:'0.88rem', fontWeight:500 }}>No match for "{search}"</div>
          </div>
        )}
      </div>
    </div>
  );
}

function tbBtn(bg, color, border) {
  return { background:bg, color, border:`1.5px solid ${border || bg}`, borderRadius:7, padding:'5px 13px', fontSize:'0.76rem', fontWeight:600, cursor:'pointer' };
}
